/**
 * frontend/src/pages/valuation/_NormalizeAddBacks.tsx
 *
 * VAL-U2C-SCREEN-1 — the add-back list and its form. A reason is required (Save
 * stays disabled without one). The list is whatever the server returned after
 * the last call, with who added each one and when; Remove is a soft delete.
 */
import { useState } from 'react'
import type { AddBack, AddBackBody, AddBackCategory } from '@/api/valuationNormalization'
import { money } from './_NormalizeYearTable'
import { HelpDot } from '@/components/ui/help-dot'
import { HELP_TEXTS } from './_helpTexts'

export const CATEGORY_LABELS: Record<AddBackCategory, string> = {
  owner_comp: 'Owner pay',
  one_time: 'One-time item',
  non_operating: 'Non-operating item',
}
const DESC_LABEL = 'Description (what this adjustment is for)'
const FIELD = 'rounded border border-pal-line-strong px-2 py-1 text-sm'

function when(iso?: string): string {
  const d = iso ? new Date(iso) : null
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US') : ''
}

interface Props {
  years: number[]
  addBacks: AddBack[]
  removedCount: number
  busy: boolean
  onAdd: (body: AddBackBody) => Promise<void>
  onRemove: (id: string) => Promise<void>
}

export function NormalizeAddBacks({ years, addBacks, removedCount, busy, onAdd, onRemove }: Props) {
  const latest = years.length ? years[years.length - 1] : undefined
  const [category, setCategory] = useState<AddBackCategory>('owner_comp')
  const [year, setYear] = useState<string>('')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [description, setDescription] = useState('')
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [pending, setPending] = useState(false)
  const [adding, setAdding] = useState(false)

  const chosenYear = year !== '' ? Number(year) : latest
  const amountNum = Number(amount)
  const ready = reason.trim() !== '' && amount.trim() !== '' && Number.isFinite(amountNum) && chosenYear !== undefined

  async function submit() {
    if (!ready || chosenYear === undefined || adding) return
    setAdding(true)
    try {
      await onAdd({ category, year: chosenYear, amount: amountNum, reason: reason.trim(), is_pending: pending, ...(description.trim() ? { description: description.trim() } : {}) })
    } finally {
      setAdding(false)
    }
    setAmount('')
    setReason('')
    setDescription('')
    setPending(false)
  }

  async function saveDescription(a: AddBack) {
    await onAdd({
      id: a.id, category: a.category, year: a.year, amount: a.amount, reason: a.reason,
      is_pending: a.is_pending, description: (edits[a.id] ?? '').trim(),
    })
    setEdits(prev => { const { [a.id]: _gone, ...rest } = prev; return rest })
  }

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-pal-ink">Add-backs</h2>
      {addBacks.length === 0 && <p className="text-sm text-pal-ink-muted">No add-backs yet.</p>}
      <ul className="space-y-2">
        {addBacks.map(a => (
          <li key={a.id} data-testid={`add-back-${a.id}`} className="rounded border border-pal-line bg-pal-white p-2 text-sm">
            <div className="flex items-start justify-between gap-3">
              <p>
                <span className="font-medium">{CATEGORY_LABELS[a.category] ?? a.category}</span> · FY{a.year} · {money(a.amount)}
                {a.is_pending && <span className="ml-2 text-pal-warning-text">(pending — not counted)</span>}
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={() => void onRemove(a.id)}
                aria-label={`Remove ${CATEGORY_LABELS[a.category] ?? a.category} ${a.year}`}
                className="text-pal-link underline disabled:opacity-50"
              >
                Remove
              </button>
            </div>
            <p className="text-pal-ink-body">{a.reason}</p>
            <div className="mt-1 flex items-end gap-2">
              <label className="flex-1 text-xs text-pal-ink-soft">{DESC_LABEL}
                <input
                  type="text"
                  maxLength={300}
                  className={`${FIELD} mt-1 block w-full`}
                  value={edits[a.id] ?? a.description ?? ''}
                  onChange={e => setEdits(prev => ({ ...prev, [a.id]: e.target.value }))}
                />
              </label>
              <button
                type="button"
                disabled={busy || edits[a.id] === undefined}
                onClick={() => void saveDescription(a)}
                className="text-pal-link underline disabled:opacity-50"
              >
                Save description
              </button>
            </div>
            <p className="text-xs text-pal-ink-muted">Added by {a.created_by ?? 'unknown'} on {when(a.created_at)}</p>
          </li>
        ))}
      </ul>
      {removedCount > 0 && <p className="text-xs text-pal-ink-muted">{removedCount} removed</p>}

      <div className="grid grid-cols-2 gap-3 rounded-lg border border-pal-line bg-pal-surface-subtle p-3 sm:grid-cols-4">
        <div className="text-xs text-pal-ink-soft">
          <div className="flex items-center gap-1"><label htmlFor="add_back_category">Category</label><HelpDot label="Category" paragraphs={HELP_TEXTS['add_back_category']} testId="info-add_back_category" /></div>
          <select id="add_back_category" className={`${FIELD} mt-1 block w-full`} value={category} onChange={e => setCategory(e.target.value as AddBackCategory)}>
            {(Object.keys(CATEGORY_LABELS) as AddBackCategory[]).map(k => <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>)}
          </select>
        </div>
        <div className="text-xs text-pal-ink-soft">
          <div className="flex items-center gap-1"><label htmlFor="add_back_year">Year</label><HelpDot label="Year" paragraphs={HELP_TEXTS['add_back_year']} testId="info-add_back_year" /></div>
          <select id="add_back_year" className={`${FIELD} mt-1 block w-full`} value={chosenYear ?? ''} onChange={e => setYear(e.target.value)}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="text-xs text-pal-ink-soft">
          <div className="flex items-center gap-1"><label htmlFor="add_back_amount">Amount</label><HelpDot label="Amount" paragraphs={HELP_TEXTS['add_back_amount']} testId="info-add_back_amount" /></div>
          <input id="add_back_amount" type="number" className={`${FIELD} mt-1 block w-full`} value={amount} onChange={e => setAmount(e.target.value)} />
        </div>
        <label className="flex items-end gap-2 text-xs text-pal-ink-soft">
          <input type="checkbox" checked={pending} onChange={e => setPending(e.target.checked)} /> Pending (not counted yet)
        </label>
        <div className="col-span-2 text-xs text-pal-ink-soft sm:col-span-4">
          <div className="flex items-center gap-1"><label htmlFor="add_back_description">{DESC_LABEL}</label><HelpDot label={DESC_LABEL} paragraphs={HELP_TEXTS['add_back_description']} testId="info-add_back_description" /></div>
          <input id="add_back_description" type="text" maxLength={300} className={`${FIELD} mt-1 block w-full`} value={description} onChange={e => setDescription(e.target.value)} />
        </div>
        <label className="col-span-2 text-xs text-pal-ink-soft sm:col-span-4">Reason
          <input type="text" className={`${FIELD} mt-1 block w-full`} value={reason} onChange={e => setReason(e.target.value)} />
        </label>
        <div className="col-span-2 sm:col-span-4">
          <button
            type="button"
            disabled={!ready || busy || adding}
            onClick={() => void submit()}
            className="rounded-md bg-blue-700 px-3 py-1.5 text-sm font-medium text-pal-white hover:bg-blue-800 disabled:opacity-50"
          >
            {adding ? 'Adding…' : 'Add add-back'}
          </button>
          {reason.trim() === '' && <span className="ml-3 text-xs text-pal-ink-muted">A reason is required for every add-back.</span>}
        </div>
      </div>
    </section>
  )
}
